import type { Category, ContentManifestV2, ContentPack, EquivalenceRule } from './types';

export const CONTENT_PACK_SCHEMA_VERSION = 2 as const;

export const CONTENT_PACK_TABLES = [
  'metadata', 'content_packs', 'categories', 'puzzles', 'puzzle_aliases',
  'puzzle_answer_words', 'puzzle_tokens', 'puzzle_word_groups', 'equivalence_rules',
] as const;

export const CONTENT_PACK_SCHEMA_SQL = `
CREATE TABLE metadata (key TEXT PRIMARY KEY, value TEXT NOT NULL);
CREATE TABLE content_packs (id TEXT PRIMARY KEY, language_code TEXT NOT NULL, version INTEGER NOT NULL, title TEXT NOT NULL, description TEXT, status TEXT NOT NULL, imported_at TEXT NOT NULL);
CREATE TABLE categories (id TEXT PRIMARY KEY, language_code TEXT NOT NULL, title TEXT NOT NULL, description TEXT, icon_key TEXT, sort_order INTEGER NOT NULL, is_active INTEGER NOT NULL);
CREATE TABLE puzzles (id TEXT PRIMARY KEY, content_pack_id TEXT NOT NULL, category_id TEXT NOT NULL, language_code TEXT NOT NULL, title_internal TEXT NOT NULL, answer_display TEXT NOT NULL, answer_normalized TEXT NOT NULL, source_text TEXT NOT NULL, difficulty_estimated INTEGER NOT NULL, is_daily_eligible INTEGER NOT NULL, status TEXT NOT NULL, sort_order INTEGER, revision INTEGER NOT NULL, created_at TEXT, updated_at TEXT);
CREATE TABLE puzzle_aliases (id TEXT PRIMARY KEY, puzzle_id TEXT NOT NULL, display TEXT NOT NULL, normalized TEXT NOT NULL, type TEXT NOT NULL, is_full_answer INTEGER NOT NULL);
CREATE TABLE puzzle_answer_words (id TEXT PRIMARY KEY, puzzle_id TEXT NOT NULL, word_index INTEGER NOT NULL, word_display TEXT NOT NULL, word_normalized TEXT NOT NULL, equivalence_group_key TEXT NOT NULL, can_be_revealed_by_player INTEGER NOT NULL, can_be_revealed_by_hint INTEGER NOT NULL);
CREATE TABLE puzzle_tokens (id TEXT PRIMARY KEY, puzzle_id TEXT NOT NULL, token_index INTEGER NOT NULL, token_type TEXT NOT NULL, original_text TEXT NOT NULL, normalized_text TEXT, equivalence_group_key TEXT, word_group_key TEXT, char_length INTEGER NOT NULL, is_revealable INTEGER NOT NULL, is_hint_eligible INTEGER NOT NULL, is_answer_word INTEGER NOT NULL, is_manually_excluded INTEGER NOT NULL, answer_word_index INTEGER);
CREATE TABLE puzzle_word_groups (id TEXT PRIMARY KEY, puzzle_id TEXT NOT NULL, group_key TEXT NOT NULL, display_label TEXT NOT NULL, normalized_variants_json TEXT NOT NULL, original_variants_json TEXT NOT NULL, first_token_index INTEGER NOT NULL, occurrence_count INTEGER NOT NULL, is_revealable INTEGER NOT NULL, is_hint_eligible INTEGER NOT NULL, is_answer_group INTEGER NOT NULL, is_manually_excluded INTEGER NOT NULL);
CREATE TABLE equivalence_rules (id TEXT PRIMARY KEY, content_pack_id TEXT NOT NULL, puzzle_id TEXT, language_code TEXT NOT NULL, scope TEXT NOT NULL, group_key TEXT NOT NULL, variants_json TEXT NOT NULL, rule_type TEXT NOT NULL, sort_order INTEGER NOT NULL);
CREATE INDEX content_packs_language_status_idx ON content_packs(language_code, status);
CREATE INDEX categories_language_order_active_idx ON categories(language_code, sort_order, is_active);
CREATE INDEX puzzles_pack_category_language_status_idx ON puzzles(content_pack_id, category_id, language_code, status);
CREATE INDEX puzzle_aliases_puzzle_normalized_idx ON puzzle_aliases(puzzle_id, normalized);
CREATE INDEX puzzle_answer_words_puzzle_idx ON puzzle_answer_words(puzzle_id, word_index);
CREATE INDEX puzzle_tokens_puzzle_idx ON puzzle_tokens(puzzle_id, token_index);
CREATE INDEX puzzle_word_groups_puzzle_idx ON puzzle_word_groups(puzzle_id, first_token_index);
CREATE INDEX equivalence_rules_lookup_idx ON equivalence_rules(content_pack_id, puzzle_id, language_code, group_key);
`;

export interface CompiledPuzzle {
  puzzle: Record<string, string | number | null>;
  aliases: Array<Record<string, string | number | null>>;
  answerWords: Array<Record<string, string | number | null>>;
  tokens: Array<Record<string, string | number | null>>;
  wordGroups: Array<Record<string, string | number | null>>;
  equivalenceRules: Array<Record<string, string | number | null>>;
}

export interface CompiledPack {
  pack: ContentPack;
  version: number;
  generatedAt: string;
  categories: Category[];
  puzzles: CompiledPuzzle[];
  globalRules: EquivalenceRule[];
}

export function parseManifestV2(value: unknown): ContentManifestV2 {
  if (!value || typeof value !== 'object') throw new Error('Le manifeste actif est invalide.');
  const manifest = value as Partial<ContentManifestV2>;
  if (manifest.schemaVersion !== CONTENT_PACK_SCHEMA_VERSION) {
    throw new Error(`Version de manifeste refusée : ${String(manifest.schemaVersion)} (v2 requise).`);
  }
  if (!Number.isInteger(manifest.releaseId) || !Array.isArray(manifest.packs)) {
    throw new Error('Le manifeste v2 ne contient pas releaseId/packs valides.');
  }
  return manifest as ContentManifestV2;
}

export function sortManifestPacks<T extends { languageCode: string; id: string }>(packs: T[]): T[] {
  return [...packs].sort((a, b) =>
    a.languageCode.localeCompare(b.languageCode) || a.id.localeCompare(b.id));
}
